import { useQuery } from '@tanstack/react-query'
import { FlaskConical } from 'lucide-react'
import { convictionApi } from '../api/client'
import './DossierABCard.css'

interface DossierABCardProps {
  accountId: number
}

/** Split bar: LLM-scored vs keyword-only dossiers, plus an LLM-budget line. */
export function DossierABCard({ accountId }: DossierABCardProps) {
  const scorecardQuery = useQuery({
    queryKey: ['scorecard', accountId],
    queryFn: () => convictionApi.scorecard(accountId),
    retry: 1,
    refetchInterval: 30000,
  })
  const statusQuery = useQuery({
    queryKey: ['conviction-status', accountId],
    queryFn: () => convictionApi.status(accountId),
    retry: 1,
    refetchInterval: 30000,
  })

  const ab = scorecardQuery.data?.dossier_ab
  const total = ab?.dossiers ?? 0
  const llmScored = ab?.llm_scored ?? 0
  const keywordOnly = ab?.keyword_only ?? 0
  const llmPct = total > 0 ? (llmScored / total) * 100 : 0
  const keywordPct = total > 0 ? (keywordOnly / total) * 100 : 0

  const budgetTotal = statusQuery.data?.llm_daily_budget_usd ?? 0
  const budgetRemaining = statusQuery.data?.llm_budget_remaining_usd ?? null

  const isLoading = scorecardQuery.isLoading
  const isError = scorecardQuery.isError

  return (
    <div className="card dossier-ab-card">
      <div className="card-header">
        <h3 className="card-title">
          <FlaskConical size={18} className="icon" aria-hidden="true" />
          Dossier method A/B (keyword vs LLM)
        </h3>
      </div>

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : isError ? (
        <div className="empty-state">
          <div className="empty-icon"><FlaskConical size={28} aria-hidden="true" /></div>
          <div className="empty-text">dossier data unavailable</div>
        </div>
      ) : total === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><FlaskConical size={28} aria-hidden="true" /></div>
          <div className="empty-text">no dossiers yet</div>
        </div>
      ) : (
        <>
          <div className="dossier-ab-bar">
            <div className="dossier-ab-bar-llm" style={{ width: `${llmPct}%` }} />
            <div className="dossier-ab-bar-keyword" style={{ width: `${keywordPct}%` }} />
          </div>
          <div className="dossier-ab-legend">
            <span className="dossier-ab-legend-item">
              <span className="dossier-ab-swatch dossier-ab-swatch--llm" />
              LLM-scored <span className="num">{llmScored}</span>
            </span>
            <span className="dossier-ab-legend-item">
              <span className="dossier-ab-swatch dossier-ab-swatch--keyword" />
              Keyword-only <span className="num">{keywordOnly}</span>
            </span>
            <span className="caption">of <span className="num">{total}</span> dossiers (all-time)</span>
          </div>

          {/* "spent today" (cap − remaining), never "remaining / total": a FULL
              budget rendered as "$2.00 / $2.00 left" reads as spent/total —
              i.e. exhausted — and was reported as "the budget never resets".
              Spend only occurs on monthly discovery days, so this line is $0.00
              nearly all the time; say so instead of implying daily activity. */}
          <div
            className="dossier-ab-budget"
            title="Daily cap for dossier LLM scoring. Spend occurs on discovery days (first trading day of the month); the counter resets at 00:00 UTC."
          >
            LLM budget{' '}
            <span className="num">
              {budgetRemaining !== null
                ? `$${Math.max(0, budgetTotal - budgetRemaining).toFixed(2)} spent today`
                : '— spent today'} · ${budgetTotal.toFixed(2)} daily cap
            </span>
          </div>
        </>
      )}
    </div>
  )
}
